import React, { useEffect, useRef, useState } from 'react';
import {
  RichTextEditorComponent,
  Toolbar,
  Inject,
  Image,
  Link,
  HtmlEditor,
  Count,
  QuickToolbar,
  Table
} from '@syncfusion/ej2-react-richtexteditor';
import i18n from 'i18next';
import { L10n } from '@syncfusion/ej2-base';

import { FONT_FAMILY } from '../../constants';
import { FORMAT, TEXT_EDITOR_ES, FONT_SIZES } from "../../constants/richtexteditor";

// RichTextEditor items list
const items = [
  "FontName",
  "Formats",
  "Bold",
  "Italic",
  "Underline",
  "StrikeThrough",
  "FontColor",
  "Alignments",
  "OrderedList",
  "UnorderedList",
  "Outdent",
  "Indent",
  "CreateLink",
  "Image",
  "BackgroundColor",
  "LowerCase",
  "UpperCase",
  "InsertCode",
  "SourceCode",
  "|",
  "FontSize",
  "SuperScript",
  "SubScript",
  "|",
  "CreateTable",
  "|",
  "ClearFormat",
  "Print",
  "FullScreen",
  "|",
  "Undo",
  "Redo"
];

//RichTextEditor ToolbarSettings
const toolbarSettings = {
  items: items,
  type: 'MultiRow'
};

const UserSign = ({
  onChange,
  defaultValue = ''
}) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState(defaultValue || '');
  const [editorValue, setEditorValue] = useState(defaultValue || '');
  const lastEmittedValue = useRef(defaultValue || '');

  const CUSTOM_FONT_FAMILY = FONT_FAMILY;
  CUSTOM_FONT_FAMILY.default = 'Arial';

  useEffect(() => {
    L10n.load(TEXT_EDITOR_ES);
  }, []);

  useEffect(() => {
    const normalized = defaultValue || '';
    if (normalized === lastEmittedValue.current) return;
    lastEmittedValue.current = normalized;
    setEditorValue(normalized);
    setHtmlDraft(normalized);
  }, [defaultValue]);

  const applyHtmlDraft = () => {
    lastEmittedValue.current = htmlDraft;
    setEditorValue(htmlDraft);
    onChange && onChange(htmlDraft);
    setIsHtmlMode(false);
  };

  const handleEditorActionBegin = (args) => {
    if (args.requestType === 'SourceCode') {
      args.cancel = true; // Prevent the default source code toggle behavior
      setIsHtmlMode(true);
    }
  };

  return (
    <>
      <div className="signature-html-tools">
        <button
          type="button"
          className="html-toggle-btn"
          onClick={() => setIsHtmlMode((prev) => !prev)}
        >
          {isHtmlMode ? i18n.t('account-sign.back_to_visual_editor') : i18n.t('account-sign.edit_html')}
        </button>
      </div>

      {isHtmlMode && (
        <div className="signature-html-editor">
          <textarea
            value={htmlDraft}
            onChange={(e) => setHtmlDraft(e.target.value)}
            spellCheck={false}
          />
          <div className="signature-html-actions">
            <button
              type="button"
              className="html-apply-btn"
              onClick={applyHtmlDraft}
            >
              {i18n.t('account-sign.apply_html')}
            </button>
          </div>
        </div>
      )}
      <RichTextEditorComponent
        id='toolsRTE'
        height={250}
        showCharCount={false}
        locale={i18n.language.startsWith('es') ? 'es' : i18n.language}
        toolbarSettings={toolbarSettings}
        fontFamily={CUSTOM_FONT_FAMILY}
        insertImageSettings={{ saveFormat: 'Base64' }}
        format={FORMAT}
        fontSize={FONT_SIZES}
        value={editorValue}
        actionBegin={(args) => {
          handleEditorActionBegin(args);
        }}
        readonly={false}
        change={content => {
          lastEmittedValue.current = content.value;
          setHtmlDraft(content.value);
          onChange(content.value);
        }}>
        <Inject
          services={[
            Toolbar,
            Image,
            Link,
            HtmlEditor,
            Count,
            QuickToolbar,
            Table
          ]}
        />
      </RichTextEditorComponent>
      <style>{`
        .signature-html-tools {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 8px;
        }

        .html-toggle-btn,
        .html-apply-btn {
          border: 1px solid var(--gray-blue);
          background: var(--white);
          color: var(--blue-lefebvre);
          border-radius: 6px;
          height: 32px;
          padding: 0 12px;
          font-size: 12px;
          cursor: pointer;
          transition: background-color 120ms ease, border-color 120ms ease;
        }

        .html-toggle-btn:hover,
        .html-apply-btn:hover {
          background: var(--ghost-white);
          border-color: var(--blue-700);
        }

        .html-toggle-btn:focus,
        .html-apply-btn:focus {
          outline: none;
          box-shadow: 0 0 0 2px var(--tertiary-byzantine-blue-050);
        }

        .signature-html-editor {
          border: 1px solid var(--whisper-blue);
          border-radius: 8px;
          padding: 8px;
          margin-bottom: 8px;
          background: var(--ghost-white);
        }

        .signature-html-editor textarea {
          width: 100%;
          min-height: 170px;
          border: 1px solid var(--chill-air);
          border-radius: 6px;
          padding: 10px;
          resize: vertical;
          font-family: Monaco, Menlo, Consolas, monospace;
          font-size: 12px;
          line-height: 1.45;
          background: var(--white);
          color: var(--grey-800);
        }

        .signature-html-editor textarea:focus {
          outline: none;
        }

        .signature-html-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
        }

        #toolsRTE {
          ${'' /* keep visual editor visible but locked while HTML mode is active */}
          opacity: ${isHtmlMode ? 0.55 : 1};
          pointer-events: ${isHtmlMode ? 'none' : 'auto'};
          #toolsRTE_toolbar {
            position: relative !important;
          }
          #toolsRTE_source-view {
            height: 169px !important;
          }
        }
        #toolsRTE.e-rte-full-screen {
          #toolsRTE_source-view {
            height: 100% !important;
            border: none;
            .e-rte-srctextarea.e-lib.e-keyboard {
              height: 100% !important;
              min-height: 80vh !important;
            }
          }
        }
        .e-rte-field .e-checkbox-wrapper {
          display: none;
        }
        .e-richtexteditor.e-rte-full-screen .e-rte-content, 
        .e-richtexteditor.e-rte-full-screen .e-source-content {
          height: 100%;
        }
        .e-richtexteditor .e-rte-content .e-content h1,
        .e-richtexteditor .e-source-content .e-content h1 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h2,
        .e-richtexteditor .e-source-content .e-content h2 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h3,
        .e-richtexteditor .e-source-content .e-content h3 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h4,
        .e-richtexteditor .e-source-content .e-content h4 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h5,
        .e-richtexteditor .e-source-content .e-content h5 {
          font-weight: 700;
        }
        .e-richtexteditor .e-rte-content .e-content h6,
        .e-richtexteditor .e-source-content .e-content h6 {
          font-weight: 700;
        }
      `}</style>
    </>
  );
};

export default UserSign;
